import { useEffect, useState } from 'react'
import { getDashboardStats } from '../api'

const fmt = (n: number) => Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const firstOfMonth = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
const today = () => new Date().toISOString().slice(0, 10)

function BarChart({ data }: { data: { month: string, revenue: number, count: number }[] }) {
  if (!data.length) return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>No data</div>
  const max = Math.max(...data.map(d => d.revenue))
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, padding: '0 8px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ fontSize: 9, color: '#64748b' }}>{fmt(d.revenue)}</div>
          <div style={{ width: '100%', background: '#2563eb', borderRadius: '3px 3px 0 0', height: max > 0 ? `${(d.revenue / max) * 140}px` : '2px', minHeight: 2, transition: 'height 0.3s' }} />
          <div style={{ fontSize: 9, color: '#64748b', whiteSpace: 'nowrap' }}>{d.month}</div>
          <div style={{ fontSize: 8, color: '#94a3b8' }}>{d.count} inv</div>
        </div>
      ))}
    </div>
  )
}

function TopCustomers({ data }: { data: { name: string, total: number }[] }) {
  if (!data.length) return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>No data</div>
  const max = Math.max(...data.map(d => d.total))
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
            <span style={{ fontWeight: 600, color: '#1e293b' }}>{d.name}</span>
            <span style={{ color: '#2563eb', fontWeight: 700 }}>{fmt(d.total)}</span>
          </div>
          <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
            <div style={{ background: '#2563eb', borderRadius: 4, height: 6, width: `${(d.total / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export default function Dashboard() {
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [fromDate, setFromDate] = useState(firstOfMonth())
  const [toDate, setToDate] = useState(today())

  const loadStats = () => {
    setLoading(true)
    getDashboardStats(fromDate, toDate).then((r: any) => { setStats(r.data); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => { loadStats() }, []) // eslint-disable-line

  const handleFromDate = (val: string) => {
    setFromDate(val)
    if (val > toDate) setToDate(val)
  }

  // Summary stats
  const totalRevenue = (stats?.monthly_revenue || []).reduce((s: number, d: any) => s + d.revenue, 0)
  const totalInvoices = (stats?.monthly_revenue || []).reduce((s: number, d: any) => s + d.count, 0)

  if (loading) return <div className="page" style={{ color: '#94a3b8' }}>Loading dashboard...</div>
  if (!stats) return <div className="page" style={{ color: '#ef4444' }}>Failed to load stats.</div>

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Business overview</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">From</label>
            <input type="date" className="form-input" value={fromDate} onChange={e => handleFromDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">To</label>
            <input type="date" className="form-input" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '8px 0' }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#16a34a' }}>{fmt(totalRevenue)}</div>
              <div style={{ fontSize: 11, color: '#4ade80' }}>Total Revenue</div>
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#2563eb' }}>{totalInvoices}</div>
              <div style={{ fontSize: 11, color: '#60a5fa' }}>Invoices</div>
            </div>
          </div>
          <button className="btn btn-primary" style={{ height: 38 }} onClick={loadStats}>🔍 Search</button>
        </div>
      </div>

      {/* Monthly Revenue */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>Monthly Revenue</div>
        <BarChart data={stats?.monthly_revenue || []} />
      </div>

      {/* Top Customers */}
      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 16, fontSize: 14 }}>Top 5 Customers by Revenue</div>
        <TopCustomers data={stats.top_customers} />
      </div>
    </div>
  )
}
